import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import Button from '../../components/ui/Button'
import FormError from '../../components/ui/FormError'
import Input from '../../components/ui/Input'
import ProviderSettingsLayout from '../../components/provider/ProviderSettingsLayout'
import {
  MY_PROVIDER_MANAGEMENT_QUERY,
  SET_PROVIDER_SERVICES_MUTATION,
  type MyProviderManagementData,
  type SetProviderServicesData,
  type SetProviderServicesVariables,
} from '../../graphql/providerManagement'
import { SERVICE_CATEGORIES_QUERY, type ServiceCategoriesData } from '../../graphql/services'
import { useLang, useScopedTranslations } from '../../contexts/LangContext'
import { localized } from '../../lib/i18n'

interface DraftService {
  id: string
  serviceTypeId: string
  categoryId: string
  customPrice: string
  customDuration: string
  defaultPriceCents: number
  defaultDurationMinutes: number
  label: string
  categoryLabel: string
}

const translations = {
  el: {
    title: 'Υπηρεσίες',
    body: 'Ρύθμισε τι προσφέρεις, με ποιες προσαρμοσμένες τιμές και διάρκεια όπου χρειάζεται.',
    loading: 'Φορτώνουμε τις υπηρεσίες...',
    save: 'Αποθήκευση υπηρεσιών',
    saved: 'Οι υπηρεσίες ενημερώθηκαν.',
    noServices: 'Δεν έχεις προσθέσει ακόμη υπηρεσίες.',
    addService: 'Προσθήκη υπηρεσίας',
    serviceType: 'Τύπος υπηρεσίας',
    category: 'Κατηγορία',
    customPrice: 'Προσαρμοσμένη τιμή (EUR)',
    customDuration: 'Προσαρμοσμένη διάρκεια (λεπτά)',
    defaultPrice: 'Βασική τιμή',
    defaultDuration: 'Βασική διάρκεια',
    remove: 'Αφαίρεση',
    categoryPlaceholder: 'Επίλεξε κατηγορία',
    servicePlaceholder: 'Επίλεξε υπηρεσία',
    duplicateError: 'Η υπηρεσία υπάρχει ήδη στη λίστα.',
    addError: 'Επίλεξε κατηγορία και υπηρεσία.',
  },
  en: {
    title: 'Services',
    body: 'Manage what you offer, with custom price and duration overrides where needed.',
    loading: 'Loading services...',
    save: 'Save services',
    saved: 'Services updated.',
    noServices: 'No services added yet.',
    addService: 'Add service',
    serviceType: 'Service type',
    category: 'Category',
    customPrice: 'Custom price (EUR)',
    customDuration: 'Custom duration (minutes)',
    defaultPrice: 'Default price',
    defaultDuration: 'Default duration',
    remove: 'Remove',
    categoryPlaceholder: 'Choose a category',
    servicePlaceholder: 'Choose a service',
    duplicateError: 'That service is already in the list.',
    addError: 'Choose a category and a service.',
  },
} as const

function randomId() {
  return Math.random().toString(36).slice(2, 10)
}

export default function ServicesPage() {
  const { locale, t } = useLang()
  const copy = useScopedTranslations('provider.services', translations[locale])

  const [services, setServices] = useState<DraftService[]>([])
  const [categoryId, setCategoryId] = useState('')
  const [serviceTypeId, setServiceTypeId] = useState('')
  const [customPrice, setCustomPrice] = useState('')
  const [customDuration, setCustomDuration] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [saved, setSaved] = useState(false)

  const { data, loading } = useQuery<MyProviderManagementData>(MY_PROVIDER_MANAGEMENT_QUERY)
  const { data: categoriesData, loading: categoriesLoading } = useQuery<ServiceCategoriesData>(SERVICE_CATEGORIES_QUERY)
  const [setProviderServices, { loading: saving }] = useMutation<SetProviderServicesData, SetProviderServicesVariables>(SET_PROVIDER_SERVICES_MUTATION)

  useEffect(() => {
    const offerings = data?.myProviderProfile?.serviceOfferings
    if (!offerings) return

    setServices(offerings.map((offering) => ({
      id: offering.id,
      serviceTypeId: offering.serviceType.id,
      categoryId: offering.serviceType.serviceCategory.id,
      customPrice: offering.customPriceCents ? String(offering.customPriceCents / 100) : '',
      customDuration: offering.customDurationMinutes ? String(offering.customDurationMinutes) : '',
      defaultPriceCents: offering.serviceType.basePriceCents,
      defaultDurationMinutes: offering.serviceType.estimatedDurationMinutes,
      label: localized(offering.serviceType, locale),
      categoryLabel: localized(offering.serviceType.serviceCategory, locale),
    })))
  }, [data, locale])

  const categories = categoriesData?.serviceCategories ?? []
  const availableServiceTypes = useMemo(
    () => categories.find((category) => category.id === categoryId)?.serviceTypes ?? [],
    [categories, categoryId],
  )

  function addService() {
    setErrors([])
    setSaved(false)

    if (!categoryId || !serviceTypeId) {
      setErrors([copy.addError])
      return
    }

    if (services.some((service) => service.serviceTypeId === serviceTypeId)) {
      setErrors([copy.duplicateError])
      return
    }

    const category = categories.find((item) => item.id === categoryId)
    const serviceType = category?.serviceTypes.find((item) => item.id === serviceTypeId)
    if (!category || !serviceType) {
      setErrors([copy.addError])
      return
    }

    setServices((current) => [
      ...current,
      {
        id: randomId(),
        serviceTypeId: serviceType.id,
        categoryId: category.id,
        customPrice,
        customDuration,
        defaultPriceCents: serviceType.basePriceCents,
        defaultDurationMinutes: serviceType.estimatedDurationMinutes,
        label: localized(serviceType, locale),
        categoryLabel: localized(category, locale),
      },
    ])

    setCategoryId('')
    setServiceTypeId('')
    setCustomPrice('')
    setCustomDuration('')
  }

  async function handleSave() {
    setErrors([])
    setSaved(false)

    try {
      const { data: mutationData } = await setProviderServices({
        variables: {
          services: services.map((service) => ({
            serviceTypeId: service.serviceTypeId,
            customPriceCents: service.customPrice ? Math.round(Number(service.customPrice) * 100) : undefined,
            customDurationMinutes: service.customDuration ? Number(service.customDuration) : undefined,
          })),
        },
      })

      const mutationErrors = mutationData?.setProviderServices.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save services.')]
      if (mutationErrors.length > 0) {
        setErrors(mutationErrors)
        return
      }

      setSaved(true)
    } catch {
      setErrors([t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save services.')])
    }
  }

  return (
    <ProviderSettingsLayout title={copy.title} body={copy.body}>
      <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
        {loading || categoriesLoading ? (
          <p className="text-sm text-slate-500">{copy.loading}</p>
        ) : (
          <>
            <FormError errors={errors} />
            {saved && (
              <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                {copy.saved}
              </div>
            )}

            <div className="space-y-4">
              {services.length === 0 && (
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                  {copy.noServices}
                </div>
              )}

              {services.map((service, index) => (
                <article key={service.id} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-400">{service.categoryLabel}</p>
                      <h2 className="mt-2 text-lg font-bold text-slate-950">{service.label}</h2>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setSaved(false)
                        setServices((current) => current.filter((item) => item.id !== service.id))
                      }}
                    >
                      {copy.remove}
                    </Button>
                  </div>

                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    <Input
                      label={copy.customPrice}
                      type="number"
                      min="0"
                      step="0.5"
                      value={service.customPrice}
                      onChange={(event) => {
                        setSaved(false)
                        setServices((current) => current.map((item, currentIndex) => currentIndex === index ? { ...item, customPrice: event.target.value } : item))
                      }}
                      hint={`${copy.defaultPrice}: €${(service.defaultPriceCents / 100).toFixed(2)}`}
                    />
                    <Input
                      label={copy.customDuration}
                      type="number"
                      min="0"
                      step="15"
                      value={service.customDuration}
                      onChange={(event) => {
                        setSaved(false)
                        setServices((current) => current.map((item, currentIndex) => currentIndex === index ? { ...item, customDuration: event.target.value } : item))
                      }}
                      hint={`${copy.defaultDuration}: ${service.defaultDurationMinutes} ${t('λεπτά', 'minutes')}`}
                    />
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-8 rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="service-category" className="mb-2 block text-sm font-semibold text-slate-700">
                    {copy.category}
                  </label>
                  <select
                    id="service-category"
                    value={categoryId}
                    onChange={(event) => {
                      setCategoryId(event.target.value)
                      setServiceTypeId('')
                    }}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
                  >
                    <option value="">{copy.categoryPlaceholder}</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {localized(category, locale)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="service-type" className="mb-2 block text-sm font-semibold text-slate-700">
                    {copy.serviceType}
                  </label>
                  <select
                    id="service-type"
                    value={serviceTypeId}
                    onChange={(event) => setServiceTypeId(event.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
                  >
                    <option value="">{copy.servicePlaceholder}</option>
                    {availableServiceTypes.map((serviceType) => (
                      <option key={serviceType.id} value={serviceType.id}>
                        {localized(serviceType, locale)}
                      </option>
                    ))}
                  </select>
                </div>

                <Input
                  label={copy.customPrice}
                  type="number"
                  min="0"
                  step="0.5"
                  value={customPrice}
                  onChange={(event) => setCustomPrice(event.target.value)}
                />
                <Input
                  label={copy.customDuration}
                  type="number"
                  min="0"
                  step="15"
                  value={customDuration}
                  onChange={(event) => setCustomDuration(event.target.value)}
                />
              </div>

              <div className="mt-5 flex justify-end">
                <Button type="button" variant="secondary" onClick={addService}>
                  {copy.addService}
                </Button>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <Button type="button" size="lg" loading={saving} onClick={handleSave}>
                {copy.save}
              </Button>
            </div>
          </>
        )}
      </section>
    </ProviderSettingsLayout>
  )
}
